import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Markdown } from '@/components/ui/Markdown';
import { Scale, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import type { ActiveAgreement } from '@mondivago/types';
import { setItem, LS_SUPPLIER_MORD_SEEN_KEY } from '@/shared/lib/localStorage';

interface SupplierMordModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string | undefined;
  activeAgreement: ActiveAgreement | null;
  loading?: boolean;
  /** Persists the agreement acceptance via the owning hook (keeps Supabase out of the component). */
  onAccept: (userId: string, activeAgreement: ActiveAgreement, ipAddress: string) => Promise<string | null>;
  onAcknowledged?: () => void;
  /** When true, the user has already accepted the latest version — show close button instead of accept flow */
  alreadySigned?: boolean;
}

export function SupplierMordModal({
  isOpen,
  onClose,
  userId,
  activeAgreement,
  loading,
  onAccept,
  onAcknowledged,
  alreadySigned,
}: SupplierMordModalProps) {
  const [isChecked, setIsChecked] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const handleAccept = useCallback(async () => {
    if (!isChecked || !userId || !activeAgreement) return;
    setIsAccepting(true);
    setAcceptError(null);

    try {
      let ipAddress = 'unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData: { ip?: string } = await ipRes.json();
        ipAddress = ipData.ip ?? 'unknown';
      } catch {}

      const errMsg = await onAccept(userId, activeAgreement, ipAddress);
      if (errMsg) throw new Error(errMsg);

      setItem(LS_SUPPLIER_MORD_SEEN_KEY, 'true');
      onAcknowledged?.();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to record your acceptance. Please try again.';
      setAcceptError(msg);
    } finally {
      setIsAccepting(false);
    }
  }, [isChecked, userId, activeAgreement, onAccept, onAcknowledged, onClose]);

  return (
    <div
      className={`fixed inset-0 z-[600] flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'visible' : 'invisible'}`}
    >
      <div
        className={`fixed inset-0 bg-gray-900/60 backdrop-blur-md transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
      />

      <div
        className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-[32px] border border-gray-200 bg-white shadow-2xl transition-all duration-300 transform ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        <div className="shrink-0 px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-lime-900 text-lime-400 shadow-lg shadow-lime-900/15 shrink-0">
              <Scale className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="heading-3 truncate">{activeAgreement?.title ?? 'Supplier Agreement'}</h3>
              <p className="mt-1 desc-md leading-relaxed">
                {alreadySigned
                  ? 'You have already accepted this agreement. You can review it again at any time.'
                  : 'Please review the terms that govern your partnership with Mondivago.'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-lime-600" />
            </div>
          ) : activeAgreement ? (
            <div className="space-y-6">
              {/*
               * Rebuild markdown from parsed sections so we can render
               * the full document through the Markdown component.
               */}
              {(() => {
                const mdContent = activeAgreement.sections
                  .map((s) => `## ${s.title}\n\n${s.paragraphs.join('\n\n')}`)
                  .join('\n\n');
                return (
                  <div className="prose prose-sm prose-gray max-w-none [&_h2]:text-lg [&_h2]:font-black [&_h2]:tracking-tight [&_h2]:text-gray-900 [&_h2]:mt-8 [&_h2:first-child]:mt-0 [&_p]:text-sm [&_p]:font-medium [&_p]:text-gray-600 [&_p]:leading-relaxed [&_ul]:text-sm [&_ul]:font-medium [&_ul]:text-gray-600 [&_li]:mt-1">
                    <Markdown content={mdContent} />
                  </div>
                );
              })()}

              <div className="rounded-3xl border border-lime-100 bg-lime-50/50 p-5">
                <div className="flex items-start gap-3">
                  <Scale className="h-5 w-5 text-lime-700 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="heading-4 !text-lime-900">PSD2 Commercial Agent Exemption</h4>
                    <p className="mt-1.5 text-xs font-medium text-lime-800 leading-relaxed">
                      Mondivago operates under the Commercial Agent Exemption as defined in Article 3 of the Payment
                      Services Directive 2 (PSD2). Mondivago is exclusively authorized to negotiate and conclude the
                      sale of Supplier services and accept payments on behalf of the Supplier. This framework ensures
                      that all payments processed through the platform are legally recognized as direct payments to the
                      Supplier.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center pt-1">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 eyebrow text-gray-500">
                  <FileText className="h-3 w-3" />
                  Agreement {activeAgreement.version}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-10 desc-md">
              No active agreement found. Please contact support.
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-white rounded-b-[32px] px-6 py-5 space-y-4">
          {acceptError && (
            <div className="flex items-center gap-2 rounded-xl bg-red-50 p-3 text-xs font-bold text-red-600 border border-red-100">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {acceptError}
            </div>
          )}

          {alreadySigned ? (
            <div className="flex flex-col items-center gap-3">
              <Button variant="outline" onClick={onClose} className="w-full">
                Close
              </Button>
            </div>
          ) : (
            <>
              <Checkbox
                checked={isChecked}
                onChange={(e) => {
                  setIsChecked(e.target.checked);
                }}
                label={
                  <span className="text-sm font-semibold text-gray-700 leading-relaxed select-none">
                    I have read, understand, and agree to the{' '}
                    <strong className="text-gray-900">{activeAgreement?.title ?? 'Supplier Agreement'}</strong> (v
                    {activeAgreement?.version ?? 'N/A'}) governing my use of the Mondivago platform, including the
                    commission structure, payout terms, and the PSD2 Commercial Agent Exemption.
                  </span>
                }
              />

              <Button
                variant="primary"
                onClick={() => {
                  void handleAccept();
                }}
                disabled={!isChecked || isAccepting || !activeAgreement}
                isLoading={isAccepting}
                className="w-full"
                icon={CheckCircle2}
              >
                {isChecked ? 'Accept & Continue' : 'Read and agree to continue'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
